import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Alert, Box } from "@mui/material";

import { useFormikContext } from "formik";

import type { FormikInitialValuesNewRequest } from "../model/form.model";
import { useNewRequestContext } from "../state/NewRequestProvider";

import { MobilePageScaffold } from "./components/MobilePageScaffold";
import { StepFooterMobile } from "./components/StepFooterMobile";

import { PurchaseStepMobile } from "./steps/purchase/PurchaseStepMobile";
import { ObservationStepMobile } from "./steps/observation/ObservationStepMobile";
import { ProductsStepMobile } from "./steps/products/ProductsStepMobile";
import { ReviewStepMobile } from "./steps/review/ReviewStepMobile";

import { validatePurchaseStep } from "../domain/validators/validatePurchaseStep";
import { validateProductsStep } from "../domain/validators/validateProductsStep";
import { validateOrder } from "../domain/validators/validateOrder";

const labels = [
  "Dados da compra",
  "Observação",
  "Produtos",
  "Revisão",
] as const;

type StepIndex = 0 | 1 | 2 | 3;

function scrollToEl(el: HTMLElement | null) {
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "end" });
}

export function MobileWizard() {
  const { state } = useNewRequestContext();

  const formik = useFormikContext<FormikInitialValuesNewRequest>();

  const [step, setStep] = useState<StepIndex>(0);

  const [attemptedStep, setAttemptedStep] = useState<StepIndex | null>(null);

  const [attemptedSubmit, setAttemptedSubmit] = useState(false);

  const [productsGuardTick, setProductsGuardTick] = useState(0);

  // âncora no final do conteúdo (depois dos Alerts)
  const bottomRef = useRef<HTMLDivElement | null>(null);

  const customerSelected = Boolean(state.purchase.customer?.id);

  const cart = state.products.cart;

  const pricingStatus = state.products.pricingStatus;

  const stepValidation = useMemo(() => {
    if (step === 0) {
      return validatePurchaseStep(
        formik.values.purchase,
        state.purchase.customer
      );
    }

    if (step === 2) {
      return validateProductsStep({
        cart,
        pricingStatus,
      });
    }

    return { ok: true as const };
  }, [
    step,
    formik.values.purchase,
    cart,
    pricingStatus,
    state.purchase.customer,
  ]);

  const navBlockReason = useMemo(() => {
    if (attemptedStep !== step) return null;
    if (stepValidation.ok) return null;
    return stepValidation.reason ?? "Verifique os campos desta etapa.";
  }, [attemptedStep, step, stepValidation]);

  const orderValidation = useMemo(
    () =>
      validateOrder({
        purchase: formik.values.purchase,
        cart,
        pricingStatus,
        customerSelected,
      }),
    [formik.values.purchase, cart, pricingStatus, customerSelected]
  );

  const finalizeBlockReason = useMemo(() => {
    if (step !== 3) return null;

    if (formik.isSubmitting && formik.submitCount > 0)
      return "Enviando pedido…";
    if (!orderValidation.ok)
      return orderValidation.reason ?? "Pedido inválido.";

    return null;
  }, [step, formik.isSubmitting, formik.submitCount, orderValidation]);

  const operationalError = useMemo(() => {
    if (step === 0) return state.purchase.error ?? null;
    if (step === 2) return state.products.error ?? null;
    return null;
  }, [step, state.purchase.error, state.products.error]);

  const reviewInvalidReason = useMemo(() => {
    if (formik.submitCount <= 0) return null;
    if (formik.isValid) return null;
    return "Existem campos obrigatórios não preenchidos na revisão.";
  }, [formik.submitCount, formik.isValid]);

  const hasAnyBottomError = Boolean(
    navBlockReason ||
      operationalError ||
      finalizeBlockReason ||
      reviewInvalidReason
  );

  const requestScrollToBottom = useCallback(() => {
    // garante que o DOM já renderizou os Alerts antes de scrollar
    requestAnimationFrame(() => scrollToEl(bottomRef.current));
  }, []);

  function goBack() {
    setAttemptedStep(null);
    setAttemptedSubmit(false);
    setStep((s) => Math.max(0, s - 1) as StepIndex);
  }

  function goNext() {
    setAttemptedSubmit(false);
    setAttemptedStep(step);

    if (step === 2) setProductsGuardTick((t) => t + 1);

    // sempre que tentar avançar, se bloquear, a mensagem fica no final:
    if (!stepValidation.ok) {
      requestScrollToBottom();
      return;
    }

    setAttemptedStep(null);
    setStep((s) => Math.min(3, s + 1) as StepIndex);
  }

  // clique em FINALIZAR (submit): marca tentativa e, se houver bloqueio/erro, desce pro final
  const onAttemptSubmit = useCallback(() => {
    setAttemptedStep(null);
    setAttemptedSubmit(true);

    // Se já sabemos que está inválido, já desce pro final antes mesmo do submit concluir
    if (!orderValidation.ok) requestScrollToBottom();
  }, [orderValidation.ok, requestScrollToBottom]);

  // Se o usuário tentou avançar/finalizar e surgiu um erro/bloqueio, garante scroll pro final
  useEffect(() => {
    const triedSomething = attemptedStep === step || attemptedSubmit;
    if (!triedSomething) return;
    if (!hasAnyBottomError) return;
    requestScrollToBottom();
  }, [
    attemptedStep,
    attemptedSubmit,
    step,
    hasAnyBottomError,
    requestScrollToBottom,
  ]);

  const content = (() => {
    switch (step) {
      case 0:
        return <PurchaseStepMobile />;
      case 1:
        return <ObservationStepMobile />;
      case 2:
        return <ProductsStepMobile guardTick={productsGuardTick} />;
      case 3:
        return <ReviewStepMobile />;
      default:
        return null;
    }
  })();

  const isStepLoading = useMemo(() => {
    if (step === 0)
      return state.purchase.loadingCustomer || state.purchase.loadingDates;
    if (step === 2) return state.products.pricingStatus === "loading";
    return false;
  }, [
    step,
    state.purchase.loadingCustomer,
    state.purchase.loadingDates,
    state.products.pricingStatus,
  ]);

  const disableNext =
    Boolean(operationalError) || isStepLoading || formik.isSubmitting;

  return (
    <Fragment>
      <MobilePageScaffold header={{ activeStep: step, labels: [...labels] }}>
        <Box sx={{ mb: 2 }}>{content}</Box>

        {navBlockReason ? (
          <Alert severity="error" sx={{ mb: 2, textAlign: "justify" }}>
            {navBlockReason}
          </Alert>
        ) : null}

        {operationalError ? (
          <Alert severity="error" sx={{ mb: 2, textAlign: "justify" }}>
            {operationalError}
          </Alert>
        ) : null}

        {finalizeBlockReason ? (
          <Alert severity="warning" sx={{ mb: 2, textAlign: "justify" }}>
            {finalizeBlockReason}
          </Alert>
        ) : null}

        {reviewInvalidReason ? (
          <Alert severity="error" sx={{ mb: 2, textAlign: "justify" }}>
            {reviewInvalidReason}
          </Alert>
        ) : null}

        {/* âncora final */}
        <Box ref={bottomRef} sx={{ height: 50 }} />
      </MobilePageScaffold>

      {step < 3 ? (
        <StepFooterMobile
          onBack={goBack}
          onNext={goNext}
          hideBack={step === 0}
          nextLabel="PRÓXIMO"
          disableNext={disableNext}
        />
      ) : (
        <StepFooterMobile
          onBack={goBack}
          onNext={onAttemptSubmit}
          nextType="submit"
          nextLabel="FINALIZAR"
          disableNext={!orderValidation.ok || formik.isSubmitting}
        />
      )}
    </Fragment>
  );
}
